/** Подсказка для недоступных действий и коротких пояснений к элементам интерфейса. */
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
	className?: string;
	children: ReactNode;
	content: string;
	side?: "top" | "bottom" | "left" | "right";
	/** Показ по команде (например, по клику): задан — наведение подсказку не открывает */
	open?: boolean;
}

export const Tooltip = ({
	children,
	className = "",
	content,
	side = "bottom",
	open,
}: TooltipProps) => {
	const [hovered, setIsOpen] = useState(false);
	const isOpen = open ?? hovered;
	const [position, setPosition] = useState<CSSProperties | null>(null);
	const root = useRef<HTMLSpanElement>(null);

	useEffect(() => {
		if (!isOpen) return;
		const updatePosition = () => {
			const bounds = root.current?.getBoundingClientRect();
			if (!bounds) return;
			const centerX = bounds.left + bounds.width / 2;
			const centerY = bounds.top + bounds.height / 2;
			setPosition(
				side === "right"
					? { top: centerY, left: bounds.right + 8, transform: "translateY(-50%)" }
					: side === "left"
						? {
								top: centerY,
								right: window.innerWidth - bounds.left + 8,
								transform: "translateY(-50%)",
							}
						: side === "top"
							? { top: bounds.top - 8, left: centerX, transform: "translate(-50%, -100%)" }
							: { top: bounds.bottom + 8, left: centerX, transform: "translateX(-50%)" },
			);
		};
		updatePosition();
		window.addEventListener("resize", updatePosition);
		window.addEventListener("scroll", updatePosition, true);
		return () => {
			window.removeEventListener("resize", updatePosition);
			window.removeEventListener("scroll", updatePosition, true);
		};
	}, [isOpen, side]);

	return (
		<span
			ref={root}
			className={`relative inline-flex ${className}`}
			onPointerEnter={() => setIsOpen(true)}
			onPointerLeave={() => setIsOpen(false)}
		>
			{children}
			{isOpen &&
				position !== null &&
				createPortal(
					<span
						className="pointer-events-none fixed z-100 w-max max-w-56 rounded-lg bg-overlay px-2 py-1 text-center text-xs text-overlay-ink shadow-lg"
						role="tooltip"
						style={position}
					>
						{content}
					</span>,
					document.body,
				)}
		</span>
	);
};
